import { KeyboardEvent, useEffect, useState } from "react";
import { RIF, useTranslation } from "../../lib";
// A type, so nothing is added to this module's runtime imports -- see the note below about the
// `lib` <-> `store` barrel cycle.
import type { RequestResult } from "../../lib/create_action";
import { useSelectTheme } from "../../store";
// Imported from the defining module rather than through the `..` barrel: these are read at module
// scope, and a value that arrives through the `lib` <-> `store` barrel cycle is `undefined` at
// evaluation time (docs/solutions/store/barrel-import-init-cycle.md).
import {
  CURRENT_PASSWORD_MIN_LENGTH,
  CurrentMfaProof,
  FreshAuthenticationProofPayload,
  OTP_VERIFICATION_CODE_LENGTH,
  RECOVERY_VERIFICATION_CODE_LENGTH,
} from "../../model/auth_proof";
import { Input, LabelBox } from "..";

/** The only two fields the backend's 403 may name. */
export const CONFIRM_ITS_YOU_FIELD_LIST = ["currentPassword", "currentMfaProof"] as const;
export type ConfirmItsYouField = (typeof CONFIRM_ITS_YOU_FIELD_LIST)[number];
export type ConfirmItsYouFieldErrors = Partial<Record<ConfirmItsYouField, string>>;

export interface ConfirmItsYouValue {
  /** Spread straight into the request body; `{}` until a field is complete. */
  proof: FreshAuthenticationProofPayload;
  /** Whether the host's submit control may be enabled. */
  canSubmit: boolean;
  /**
   * A save that carried everything the identity could prove needs no `lastSecurityChange`
   * mark, because the backend had what it needed to keep the other sessions alive. Anything less
   * -- no password, or an MFA-enabled identity that left the code blank -- does.
   */
  proofIsComplete: boolean;
}

/** What the host reports to itself before the molecule has said anything, and after it clears. */
export const CONFIRM_ITS_YOU_EMPTY_VALUE: ConfirmItsYouValue = {
  proof: {},
  canSubmit: true,
  proofIsComplete: false,
};

const isConfirmItsYouField = (field: unknown): field is ConfirmItsYouField =>
  CONFIRM_ITS_YOU_FIELD_LIST.includes(field as ConfirmItsYouField);

/**
 * A 403's hint is shown next to the field it names, but only when that field is one of the
 * two the contract defines and only when a hint actually came with it. Anything else -- an unknown
 * field, a missing hint, a payload shaped in some way we did not expect -- is reported as the
 * generic error the host renders under the form, so a backend that grows a third field cannot
 * silently drop its message on the floor.
 */
export const mapConfirmItsYouForbidden = (
  payload: unknown,
): { fieldErrors: ConfirmItsYouFieldErrors; generic: boolean } => {
  const body = (payload ?? {}) as { clientData?: unknown; errorHint?: unknown };
  const clientData = (body.clientData ?? {}) as { field?: unknown };
  const field =
    typeof clientData === "object" && clientData !== null ? clientData.field : undefined;
  const hint = typeof body.errorHint === "string" ? body.errorHint.trim() : "";
  if (!isConfirmItsYouField(field) || !hint) return { fieldErrors: {}, generic: true };
  return { fieldErrors: { [field]: hint }, generic: false };
};

/**
 * What one security-change reply means to the confirm prompt's two hosts, read once instead of
 * twice. How a 403 maps onto the fields, and when a save must be marked so a later sign-out can
 * name it (`lastSecurityChange`), are the same in the confirm pop and the email page; everything
 * else about a failure is not, so this says nothing about it. A 500 is a single
 * line in the pop and one of four in the email flow, and neither host should have to know the
 * other's map to keep this one in step.
 */
export type ConfirmItsYouOutcome =
  /**
   * A 2xx. When `recordIncompleteProof` is set, dispatch `SESSION_LAST_SECURITY_CHANGE_SET`: the
   * save went out without a complete proof, so a later sign-out may be this change's doing.
   */
  | { kind: "saved"; recordIncompleteProof: boolean }
  /** Render `fieldErrors` through the molecule and `formError` under the form. */
  | { kind: "proof_refused"; fieldErrors: ConfirmItsYouFieldErrors; formError: string | null }
  /** None of the shared rules apply: the host's own failure map owns this reply. */
  | { kind: "unhandled" };

export interface ConfirmItsYouOutcomeContext {
  /**
   * `proofIsComplete` of the value that was SENT. Never the live one: the fields stay editable
   * while the request is out, so what is on screen when the reply lands may not be what went.
   */
  sentProofWasComplete: boolean;
  t: (key: string) => string;
}

export const readConfirmItsYouOutcome = (
  requestResult: RequestResult,
  { sentProofWasComplete, t }: ConfirmItsYouOutcomeContext,
): ConfirmItsYouOutcome => {
  // only a save the identity could not fully prove may be the one a later sign-out has to
  // name. A complete proof means the backend kept the other sessions, so there is nothing to
  // explain on the login page. Nothing is signed out client-side from either host.
  if (requestResult.success) return { kind: "saved", recordIncompleteProof: !sentProofWasComplete };

  const statusCode = requestResult.result?.statusCode ?? null;
  if (statusCode !== 403) return { kind: "unhandled" };

  // the proof was refused. Name the field the backend named and leave everything else
  // standing, so the user fixes one field instead of starting over.
  const { fieldErrors, generic } = mapConfirmItsYouForbidden(requestResult.result?.payload);
  return {
    kind: "proof_refused",
    fieldErrors,
    formError: generic ? t("security_change.confirm_error_generic") : null,
  };
};

/** Length alone picks the factor, exactly as `currentMfaProofSchema` reads it. */
const proofFromCode = (code: string): CurrentMfaProof | null => {
  if (code.length === OTP_VERIFICATION_CODE_LENGTH) return { type: "OTP", verificationCode: code };
  if (code.length === RECOVERY_VERIFICATION_CODE_LENGTH)
    return { type: "RECOVERY", verificationCode: code };
  return null;
};

export interface ConfirmItsYouFieldsPropsT {
  /** The identity's CURRENT MFA setting, not the one being saved: it is what can be proved today. */
  mfaEnabled: boolean;
  /** Hints from a 403, already whitelisted by `mapConfirmItsYouForbidden`. */
  fieldErrors?: ConfirmItsYouFieldErrors;
  onChange: (value: ConfirmItsYouValue) => void;
}

/**
 * The one confirm prompt, mounted by both security flows. It collects the fresh-authentication
 * proof, derives the factor from the code's length and shows the backend's 403 hints
 * next to the field they name.
 *
 * The password and the code live here in React state and nowhere else. They leave only
 * through `onChange`, which the host spreads into the one request that carries them; they are
 * never dispatched on their own, never stored, never logged. Unmounting drops them, and both
 * hosts unmount this on cancel and on success.
 */
export const ConfirmItsYouFields = (props: ConfirmItsYouFieldsPropsT) => {
  const { mfaEnabled, fieldErrors, onChange } = props;
  const { t } = useTranslation();
  const { color, pad } = useSelectTheme();

  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codeRevealed, setCodeRevealed] = useState(false);

  // Pasted codes routinely arrive with padding, and a trailing space would otherwise read as the
  // wrong length. The password is never trimmed: spaces in it are the user's, not an accident.
  const trimmedCode = code.trim();
  const mfaProof = proofFromCode(trimmedCode);
  const codeLengthError = mfaEnabled && trimmedCode.length > 0 && mfaProof === null;

  const passwordAccepted = password.length >= CURRENT_PASSWORD_MIN_LENGTH;
  const mfaProofAccepted = mfaEnabled && mfaProof !== null;

  const mfaProofHint = fieldErrors?.currentMfaProof;
  // A hint for a field the user cannot see is a hint nobody reads, so the 403 opens the disclosure.
  useEffect(() => {
    if (mfaProofHint) setCodeRevealed(true);
  }, [mfaProofHint]);

  useEffect(() => {
    onChange({
      proof: {
        ...(passwordAccepted ? { currentPassword: password } : {}),
        ...(mfaProofAccepted ? { currentMfaProof: mfaProof as CurrentMfaProof } : {}),
      },
      // 8 characters locally, so a typed password can never be the thing the saga schema
      // rejects; a half-typed code blocks submit rather than going out to be refused.
      canSubmit: passwordAccepted && !codeLengthError,
      proofIsComplete: passwordAccepted && (!mfaEnabled || mfaProofAccepted),
    });
  }, [mfaEnabled, password, trimmedCode]);

  const codeRegionId = "confirm_its_you_code_region";

  // A native button already fires a click on Enter and on Space, so the key handler suppresses
  // that default: without the preventDefault the browser would toggle the disclosure twice, once
  // here and once on the click it synthesises. jsdom never synthesises it, which is why the
  // handler has to exist at all for the keyboard path to work.
  const handleDisclosureKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
    event.preventDefault();
    setCodeRevealed((revealed) => !revealed);
  };

  return (
    <div data-testid="confirm_its_you_fields" css={{ marginBottom: pad.medium }}>
      <div data-testid="confirm_its_you_password_field" css={{ marginBottom: pad.medium }}>
        <LabelBox
          {...{
            label: t("security_change.confirm_password_label"),
            warningMsg: fieldErrors?.currentPassword ?? "",
          }}
        >
          <Input
            data-testid="confirm_its_you_password_input"
            type="password"
            autoComplete="current-password"
            placeholder={t("security_change.confirm_password_placeholder")}
            hasError={!!fieldErrors?.currentPassword}
            value={password}
            onChange={(event: { target: { value: string } }) => setPassword(event.target.value)}
          />
        </LabelBox>
      </div>

      {/* Every MFA-enabled identity sees this. The backend strips `secret*` from the identity
          it returns, so the client cannot tell an authenticator account from an SMS or email one
          and must not promise that this browser is spared. "May", never "will". */}
      {RIF(
        mfaEnabled,
        <>
          <div css={{ color: color.grey_400, marginBottom: pad.small }}>
            {t("security_change.confirm_mfa_warning")}
          </div>
          <button
            type="button"
            data-testid="confirm_its_you_code_disclosure"
            aria-expanded={codeRevealed}
            aria-controls={codeRegionId}
            onClick={() => setCodeRevealed((revealed) => !revealed)}
            onKeyDown={handleDisclosureKeyDown}
            css={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: 0,
              border: "none",
              background: "none",
              font: "inherit",
              textAlign: "left",
              color: color.primary,
              cursor: "pointer",
            }}
          >
            <span aria-hidden="true">{codeRevealed ? "▾" : "▸"}</span>
            {t("security_change.confirm_code_disclosure")}
          </button>
          <div id={codeRegionId} data-testid="confirm_its_you_code_field">
            {RIF(
              codeRevealed,
              <div css={{ marginTop: pad.small }}>
                <LabelBox
                  {...{
                    label: t("security_change.confirm_code_label"),
                    warningMsg: codeLengthError
                      ? t("security_change.confirm_code_length_error")
                      : (fieldErrors?.currentMfaProof ?? ""),
                  }}
                >
                  <Input
                    data-testid="confirm_its_you_code_input"
                    type="text"
                    // recovery codes are alphanumeric, so a numeric keypad would be the wrong one
                    inputMode="text"
                    autoComplete="one-time-code"
                    placeholder={t("security_change.confirm_code_placeholder")}
                    hasError={codeLengthError || !!fieldErrors?.currentMfaProof}
                    value={code}
                    onChange={(event: { target: { value: string } }) => setCode(event.target.value)}
                  />
                </LabelBox>
                <div css={{ color: color.grey_400, fontSize: "14px", marginTop: pad.small }}>
                  {t("security_change.confirm_code_hint")}
                </div>
              </div>,
            )}
          </div>
        </>,
      )}
    </div>
  );
};
